import React, { useState, useEffect } from 'react';
import { useCreateApiKey } from '../../hooks/use-api-keys';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import { CreateApiKeyRequest } from '../../types';
import { Button } from '../ui/button';
import { Input, Textarea } from '../ui/form';

interface ApiKeyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  divisionId: string;
}

export function ApiKeyFormModal({ 
  isOpen, 
  onClose, 
  divisionId
}: ApiKeyFormModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);

  const createApiKey = useCreateApiKey(divisionId);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setExpiresAt('');
      setCreatedApiKey(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      const request: CreateApiKeyRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        expiresAt: expiresAt || undefined
      };

      const result = await createApiKey.mutateAsync(request);
      
      // Show the generated API key
      setCreatedApiKey(result.plainTextKey);
    } catch (error) {
      console.error('Failed to create API key:', error);
      // Error handling is typically done via react-query's error handling
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setExpiresAt('');
    setCreatedApiKey(null);
    setIsSubmitting(false);
    onClose();
  };

  const copyToClipboard = () => {
    if (createdApiKey) {
      navigator.clipboard.writeText(createdApiKey);
      alert('API key copied to clipboard!');
    }
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>

        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="api-key-modal-title"
          tabIndex={-1}
          className="relative inline-block transform overflow-hidden rounded-lg bg-white dark:bg-secondary-800 px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle focus:outline-none"
        >
          {createdApiKey ? (
            // Show the generated API key
            <div>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <h3 id="api-key-modal-title" className="text-lg font-medium leading-6 text-secondary-900 dark:text-white">
                  API Key Created Successfully
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">
                    Please copy and save this API key now. You won't be able to see it again!
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-4 border border-secondary-200 dark:border-secondary-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-200 mb-1">
                        API Key
                      </label>
                      <div className="font-mono text-sm text-secondary-900 dark:text-white break-all">
                        {createdApiKey}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="ml-3 flex-shrink-0 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6">
                <Button
                  type="button"
                  onClick={handleClose}
                  className="w-full"
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            // Show the form
            <div>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
                <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <h3 id="api-key-modal-title" className="text-lg font-medium leading-6 text-secondary-900 dark:text-white">
                  Create API Key
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">
                    Create a new API key to allow external systems to submit orders to this division.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="mt-5 sm:mt-6 space-y-4">
                <Input
                  label="Name"
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Production API Key"
                  required
                />

                <Textarea
                  label="Description (Optional)"
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., API key for our production order system"
                  rows={3}
                />

                <Input
                  label="Expiration Date (Optional)"
                  id="expiresAt"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  helperText="Leave empty for no expiration"
                />

                <div className="flex gap-3 mt-5 sm:mt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !name.trim()}
                    className="flex-1"
                  >
                    {isSubmitting ? 'Creating...' : 'Create API Key'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
